// COP Copilot — the CapabilityManifest (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// Declared BEFORE activation (AOA §3a). The host GATES ON THIS (manifestGate.ts): a contribution
// whose kind is not declared, or a command whose id/actors exceed what is declared here, is
// REFUSED (registered = no-op) and logged — the capability stays live (crash-isolation).
//
// WAVE-A SHAPE:
//   contributes: ['panel','command'] — NOT 'layer' (no engine write in Wave A; the copilot's
//     own highlight/evac layer is a Wave-B host-paint concern), and NOT a channel kind (voice/
//     video registerChannel is FUTURE WORK / unsupported — ContributionKindName is only
//     layer|panel|command|context-menu — so keep voice OUT or the manifest is refused).
//   permissions: a single read PolicyRequest on the unmarked/bench resource (the copilot only
//     READS the host-filtered, PDP-visible picture; it never routes/exports/downgrades).
//   commandActors: request_evac is HUMANS-ONLY at the manifest layer (the registration-time half
//     of the actor gate; the host's dispatch-time irreversible human-gate closes the other half),
//     so an ai-agent/npe can never even invoke it. The ask/submit verbs may be driven by any
//     actor class (read is safe to automate).

import type { CapabilityManifest } from "../../../../packages/contracts-ts/src/index.ts";
import { REQUEST_EVAC_CMD_ID } from "./copilotEvac.ts";

/** Stable capability id. */
export const COPILOT_CAPABILITY_ID = "scop:cap:copilot";

/** The command ids the copilot contributes. */
export const COPILOT_CMD_ASK = "scop:cmd:copilot-ask";
export const COPILOT_CMD_SUBMIT = "scop:cmd:copilot-submit";
export const COPILOT_CMD_QUERY = "scop:cmd:copilot-query";
export const COPILOT_CMD_HIGHLIGHT = "scop:cmd:copilot-highlight";
export const COPILOT_CMD_CLEAR_HIGHLIGHTS = "scop:cmd:copilot-clear-highlights";
export const COPILOT_CMD_FILTER_LAYER = "scop:cmd:copilot-filter-layer";

/** THE MANIFEST — the host gates on this so over-reach is refused while the capability stays live. */
export const COPILOT_MANIFEST: CapabilityManifest = {
  contributes: ["panel", "command"],
  permissions: [
    {
      subject: {},
      action: "read",
      resource: {
        classification: "UNCLASSIFIED",
        releasability: [],
        mission: [],
        caveats: [],
        originator_control: false,
        policy_ref: "policy://defense.local",
        payload_mode: "clear",
        leakage_profile: "clear",
      },
    },
  ],
  commandActors: {
    // The irreversible evac is HUMANS-ONLY at the manifest layer (the host's dispatch-time
    // human-gate closes the other half). An ai-agent/npe cannot invoke it at all.
    [REQUEST_EVAC_CMD_ID]: ["human"],
    // The analyst + read/query verbs are safe to automate (read-only / host-mediated, no engine write).
    [COPILOT_CMD_ASK]: ["human", "npe", "ai-agent"],
    [COPILOT_CMD_SUBMIT]: ["human", "npe", "ai-agent"],
    [COPILOT_CMD_QUERY]: ["human", "npe", "ai-agent"],
    // The rerouted maps.* verbs record host-mediated intent only (no live paint until Wave B).
    [COPILOT_CMD_HIGHLIGHT]: ["human", "npe", "ai-agent"],
    [COPILOT_CMD_CLEAR_HIGHLIGHTS]: ["human", "npe", "ai-agent"],
    [COPILOT_CMD_FILTER_LAYER]: ["human", "npe", "ai-agent"],
  },
};
